import React from 'react';
import type {
  FmMapBannerProps,
  FmMapCalloutProps,
  FmMapCanvasProps,
  FmMapForegroundGroupProps,
  FmMapForegroundRailProps,
  FmMapGlassButtonProps,
  FmMapLegendPanelProps,
  FmMapPanelRootProps,
  FmMapSnapshotProps,
  FmMapStageLabelProps,
  FmMapStageProps,
} from './FmMapStage.types';
import {
  cx,
  mapChromeClass,
  mapSnapshotState,
  mapStageStyle,
  mapStageTestIds,
} from './FmMapStage.helpers';
import { FmMapSnapshotMeta, FmMapStageActions } from './FmMapStage.parts';
import { FM_MAP_STAGE_SIZE_PX } from '@/shared/ui/mapStageSizes';
import './FmMapStage.css';
import './FmMapChrome.css';

export type {
  FmMapBannerProps,
  FmMapCalloutProps,
  FmMapCanvasProps,
  FmMapForegroundGroupProps,
  FmMapForegroundRailProps,
  FmMapGlassButtonProps,
  FmMapLegendPanelProps,
  FmMapPanelRootProps,
  FmMapSnapshotProps,
  FmMapStageLabelProps,
  FmMapStageProps,
} from './FmMapStage.types';

/** Full-bleed map host with glass route chrome and an optional solid dock panel. */
export const FmMapStage: React.FC<FmMapStageProps> = ({
  height,
  size,
  fill = false,
  foot,
  children,
  leftChrome,
  rightChrome,
  toolbar,
  dockPanel,
  title,
  subtitle,
  subtitleTestId,
  actionsTestId,
  leftChromeTestId,
  rightChromeTestId,
  className,
  'data-testid': dataTestId,
}) => {
  const testIds = mapStageTestIds(dataTestId, actionsTestId, leftChromeTestId, rightChromeTestId);
  const stageHeight = height ?? (size ? FM_MAP_STAGE_SIZE_PX[size] : undefined);

  const stage = (
    <section
      className={cx('fm-map-stage', fill && 'fm-map-stage--fill', className)}
      data-testid={dataTestId}
      data-fm-nav-theme="dark"
      style={mapStageStyle(fill, stageHeight)}
    >
      <div className="fm-map-stage__canvas">{children}</div>
      <FmMapStageActions
        leftChrome={leftChrome}
        rightChrome={rightChrome}
        title={title}
        subtitle={subtitle}
        subtitleTestId={subtitleTestId}
        actionsTestId={testIds.actions}
        leftChromeTestId={testIds.leftChrome}
        rightChromeTestId={testIds.rightChrome}
      />
      {toolbar ? <div className="fm-map-stage__toolbar">{toolbar}</div> : null}
      {dockPanel ? <div className="fm-map-stage__dock">{dockPanel}</div> : null}
    </section>
  );

  if (foot) {
    return (
      <div className={cx('fm-map-stage-wrap', fill && 'fm-map-stage-wrap--fill')}>
        {stage}
        <div className="fm-map-stage-wrap__foot">{foot}</div>
      </div>
    );
  }

  return stage;
};

export const FmMapPanelRoot: React.FC<FmMapPanelRootProps> = ({
  children,
  staticSnapshot = false,
  'data-testid': dataTestId,
}) => (
  <div
    className={cx('fm-map-panel-root', staticSnapshot && 'fm-map-panel-root--static-snapshot')}
    data-testid={dataTestId}
  >
    {children}
  </div>
);

/** True map-workspace canvas. Use inside FmMapStage, not for report snapshots. */
export const FmMapCanvas: React.FC<FmMapCanvasProps> = ({
  children,
  dark = false,
  className,
  'data-testid': dataTestId,
}) => (
  <div
    className={cx('fm-map-canvas', dark && 'fm-map-canvas--dark', className)}
    data-testid={dataTestId}
  >
    {children}
  </div>
);

/** Fitted/static report map primitive. */
export const FmMapSnapshot: React.FC<FmMapSnapshotProps> = ({
  children,
  caption,
  description,
  controls,
  legend,
  timeline,
  size,
  height,
  aspectRatio = 'wide',
  variant = 'outlined',
  fit = 'cover',
  interactionMode,
  interactive = false,
  className,
  canvasClassName,
  'data-testid': dataTestId,
}) => {
  // Normalize deprecated 'interactive' prop to 'interactionMode' internally
  const resolvedInteractionMode = interactionMode ?? (interactive ? 'interactive' : undefined);
  
  const snapshot = mapSnapshotState({
    height,
    size,
    aspectRatio,
    variant,
    fit,
    interactionMode: resolvedInteractionMode,
    className,
  });

  return (
    <figure
      className={snapshot.className}
      data-testid={dataTestId}
      data-interaction-mode={snapshot.resolvedInteractionMode}
    >
      <div
        className="fm-map-snapshot__frame"
        style={snapshot.frameStyle}
        data-testid={dataTestId ? `${dataTestId}-frame` : undefined}
        data-interaction-mode={snapshot.resolvedInteractionMode}
      >
        <div
          className={cx('fm-map-snapshot__canvas', canvasClassName)}
          aria-hidden={!snapshot.isInteractive}
          data-testid={dataTestId ? `${dataTestId}-canvas` : undefined}
          data-interaction-mode={snapshot.resolvedInteractionMode}
        >
          {children}
        </div>
      </div>
      <FmMapSnapshotMeta
        caption={caption}
        description={description}
        controls={controls}
        legend={legend}
        timeline={timeline}
      />
    </figure>
  );
};

export const FmMapStageLabel: React.FC<FmMapStageLabelProps> = ({
  title,
  subtitle,
  position = 'lower-left',
  titleTestId,
  subtitleTestId,
  className,
}) => (
  <div className={cx('fm-map-stage-label', `fm-map-stage-label--${position}`, className)}>
    <div className="fm-map-stage-label__title" data-testid={titleTestId}>
      {title}
    </div>
    {subtitle ? (
      <div className="fm-map-stage-label__subtitle" data-testid={subtitleTestId}>
        {subtitle}
      </div>
    ) : null}
  </div>
);

export const FmMapForegroundRail: React.FC<FmMapForegroundRailProps> = ({
  children,
  align = 'between',
  className,
  innerClassName,
  'data-testid': dataTestId,
}) => (
  <div className={cx('fm-map-foreground-rail', className)} data-testid={dataTestId}>
    <div
      className={cx(
        'fm-map-foreground-rail__inner',
        `fm-map-foreground-rail__inner--${align}`,
        innerClassName,
      )}
    >
      {children}
    </div>
  </div>
);

export const FmMapForegroundGroup: React.FC<FmMapForegroundGroupProps> = ({ children }) => (
  <div className="fm-map-foreground-group">{children}</div>
);

export const FmMapLegendPanel: React.FC<FmMapLegendPanelProps> = ({
  children,
  surface = 'plain',
  'data-testid': dataTestId,
}) => (
  <div className={mapChromeClass('fm-map-legend-panel', surface)} data-testid={dataTestId}>
    {children}
  </div>
);

/** Frosted-glass foreground button for map chrome (lasso/toggle over a raster). */
export const FmMapGlassButton: React.FC<FmMapGlassButtonProps> = ({
  children,
  className,
  type = 'button',
  ...rest
}) => (
  <button type={type} className={cx('fm-map-glass-button', className)} {...rest}>
    {children}
  </button>
);

export const FmMapCallout: React.FC<FmMapCalloutProps> = ({
  children,
  surface = 'plain',
  glyph = '!',
  'data-testid': dataTestId,
}) => (
  <div className={mapChromeClass('fm-map-callout', surface)} data-testid={dataTestId}>
    <span className="fm-map-callout__glyph" aria-hidden="true">
      {glyph}
    </span>
    <span className="fm-map-callout__text">{children}</span>
  </div>
);

export const FmMapBanner: React.FC<FmMapBannerProps> = ({
  children,
  align = 'center',
  className,
  'data-testid': dataTestId,
}) => (
  <div
    className={cx('fm-map-banner', `fm-map-banner--${align}`, className)}
    data-testid={dataTestId}
  >
    {children}
  </div>
);

export default FmMapStage;
